import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Bridge between the custom CHEXZ add-to-cart buttons (plain inline scripts) and the
 * theme's cart components.
 *
 * The theme components (cart drawer, cart page, cart bubble, gift offer) all listen for
 * the standard `CartLinesUpdateEvent` and re-render from the section HTML carried on the
 * event promise. A bare `fetch('/cart/add.js')` updates the server cart but leaves the UI
 * untouched until the next page load, which is why items only showed up after a refresh.
 */

const root = () => window.Shopify?.routes?.root ?? '/';

/** @returns {string[]} section ids of every cart-items-component currently on the page */
function cartSectionIds() {
  const ids = new Set();

  for (const element of document.querySelectorAll('cart-items-component')) {
    const id = /** @type {HTMLElement} */ (element).dataset.sectionId;
    if (id) ids.add(id);
  }

  return [...ids];
}

/** @returns {Promise<any>} the ajax cart */
function fetchCart() {
  return fetch(`${root()}cart.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => response.json());
}

/**
 * Adds a line to the cart and notifies the theme so every cart surface re-renders.
 *
 * @param {object} line
 * @param {number|string} line.id - Variant id.
 * @param {number} [line.quantity] - Defaults to 1.
 * @param {Record<string, string>} [line.properties]
 * @param {number|string} [line.selling_plan]
 * @param {object} [options]
 * @param {string} [options.source] - Identifier for the calling component.
 * @param {string} [options.productId]
 * @returns {Promise<any>} the refreshed ajax cart
 */
export async function addToCart(line, options = {}) {
  const quantity = Number(line.quantity) || 1;
  const sections = cartSectionIds();

  /** @type {Record<string, any>} */
  const body = { id: line.id, quantity };
  if (line.properties) body.properties = line.properties;
  if (line.selling_plan) body.selling_plan = line.selling_plan;
  if (sections.length) {
    body.sections = sections.join(',');
    body.sections_url = window.location.pathname;
  }

  const deferred = CartLinesUpdateEvent.createPromise();

  // Nothing forces a consumer to attach a handler, so keep a rejected promise from
  // bubbling up as an unhandled rejection.
  deferred.promise.catch(() => {});

  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [
        {
          merchandiseId: CartLinesUpdateEvent.createGid('ProductVariant', line.id),
          quantity,
        },
      ],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }

    const cart = await fetchCart();

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: data.sections,
        items: cart.items,
        itemCount: cart.item_count,
        source: options.source ?? 'chexz-cart-bridge',
        productId: options.productId,
        didError: false,
      },
    });

    // Legacy listeners in the custom CHEXZ markup (bottom nav badge, etc.).
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, data: { itemCount: cart.item_count } },
      })
    );

    return cart;
  } catch (error) {
    deferred.reject(error);
    document.dispatchEvent(
      new CartErrorEvent({
        error: error instanceof Error ? error.message : 'Add to cart failed',
        code: 'INVALID',
      })
    );
    throw error;
  }
}

// `chexzCartAdd` starts life as a queueing stub defined in snippets/scripts.liquid, so a
// click that lands before this module finishes loading is not lost. Take over and drain it.
const queued = window.chexzCartQueue ?? [];

window.chexzCartQueue = [];
window.chexzCartBridgeReady = true;
window.chexzCartAdd = addToCart;

for (const item of queued) {
  addToCart(item.line, item.options).then(item.resolve, item.reject);
}

document.dispatchEvent(new CustomEvent('chexz:cart-bridge-ready'));
